import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { INTEL_API, errMsg } from "./OutreachSendButton";

export const IntelligenceTools = ({ organizations, onChange }) => {
  const [status, setStatus] = useState(null);
  const [dups, setDups] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [content, setContent] = useState("");
  const [report, setReport] = useState(null);
  const [dossier, setDossier] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, d, n, r] = await Promise.all(["providers/status", "duplicates", "notifications", "research/runs"].map((k) => axios.get(`${INTEL_API}/${k}`)));
      setStatus(s.data); setDups(d.data); setNotifs(n.data); setRuns(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body, msg) => {
    try { const r = await axios.post(`${INTEL_API}/${path}`, body || {}); if (msg) toast.success(msg); load(); onChange(); return r.data; }
    catch (e) { toast.error(errMsg(e)); return null; }
  };
  const runImport = async (commit) => {
    const fmt = content.trim().startsWith("[") || content.trim().startsWith("{") ? "json" : "csv";
    const r = await post(commit ? "imports/commit" : "imports/dry-run", { format: fmt, content }, commit ? "Import validé" : null);
    if (r) setReport(r);
  };
  const openDossier = async (id) => {
    try { setDossier((await axios.get(`${INTEL_API}/organizations/${id}/dossier`)).data); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8" data-testid="intel-tools">
      <section className="space-y-2">
        <h3 className="font-medium">Recherche</h3>
        {status && !status.search.available && <p className="text-sm text-amber-700" data-testid="intel-search-disabled">Recherche web non configurée</p>}
        {status && <p className="text-xs text-muted-foreground" data-testid="intel-llm-status">IA : {status.llm.model} ({status.llm.available ? "disponible" : "indisponible"})</p>}
        <div className="flex flex-wrap gap-2">
          {organizations.map((o) => (
            <span key={o.id} className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => post("research/runs", { mode: "DEEP_DIVE", filters: { organization_id: o.id } }, "Analyse lancée")} data-testid={`intel-deepdive-${o.id}`}>Analyser {o.canonical_name}</Button>
              <Button size="sm" variant="ghost" onClick={() => openDossier(o.id)} data-testid={`intel-dossier-${o.id}`}>Dossier</Button>
            </span>
          ))}
        </div>
        <ul className="text-xs" data-testid="intel-runs-list">{runs.map((r) => <li key={r.id}>{r.mode} — {r.status}</li>)}</ul>
        {dossier && <pre className="whitespace-pre-wrap text-xs border p-3 rounded" data-testid="intel-dossier-markdown">{dossier.markdown}</pre>}
      </section>
      <section className="space-y-2">
        <h3 className="font-medium">Import CSV / JSON</h3>
        <Textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)} placeholder="canonical_name,website,contact_email" data-testid="intel-import-input" />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={!content} onClick={() => runImport(false)} data-testid="intel-import-dryrun">Simuler</Button>
          <Button size="sm" disabled={!content} onClick={() => runImport(true)} data-testid="intel-import-commit">Importer</Button>
        </div>
        {report && <pre className="text-xs" data-testid="intel-import-report">{JSON.stringify(report.summary || report.result, null, 1)}</pre>}
      </section>
      <section className="space-y-2">
        <h3 className="font-medium">Doublons</h3>
        {dups.length === 0 && <p className="text-sm text-muted-foreground">Aucun doublon ouvert</p>}
        {dups.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`intel-dup-${d.id}`}>
            <Badge variant="secondary">{d.reasons.join(", ")}</Badge><span className="font-mono text-xs">{d.org_a_id} ↔ {d.org_b_id}</span>
            <Button size="sm" onClick={() => window.confirm("Fusionner ces deux fiches ? La fiche fusionnée sera conservée et tracée.") && post(`duplicates/${d.id}/merge`, {}, "Fusion effectuée")} data-testid={`intel-dup-merge-${d.id}`}>Fusionner</Button>
            <Button size="sm" variant="ghost" onClick={() => post(`duplicates/${d.id}/dismiss`, null, "Écarté")} data-testid={`intel-dup-dismiss-${d.id}`}>Écarter</Button>
          </div>
        ))}
      </section>
      <section className="space-y-2">
        <h3 className="font-medium">Notifications</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => post("notifications/deadline-check", null, "Échéances vérifiées")} data-testid="intel-deadline-check">Vérifier les échéances</Button>
          <Button size="sm" variant="ghost" onClick={() => post("notifications/read-all", null, "Tout marqué comme lu")} data-testid="intel-notifs-read-all">Tout marquer lu</Button>
        </div>
        <ul className="text-sm" data-testid="intel-notifications-list">
          {notifs.map((n) => <li key={n.id} className={n.read_at ? "text-muted-foreground" : ""}>{n.type} — {n.title}</li>)}
        </ul>
      </section>
    </div>
  );
};
